import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pause, Play, Zap } from 'lucide-react';
import { listSchedules, pauseSchedule, resumeSchedule, runSchedule } from '@mkcore/platform/schema';
import { Badge, Button, EmptyState, ErrorState, Mono, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { JobStatusBadge } from '@/features/admin/job-status';

export const Route = createFileRoute('/_app/admin/schedules')({ component: Schedules });

const schedulesQuery = queryOptions({ queryKey: ['admin', 'schedule', 'list'], queryFn: () => call(listSchedules, {}) });

/** Recurring jobs (CO-0724): schedule, status, last and next run; pause, switch on or run now, without a deploy. */
function Schedules() {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const navigate = useNavigate();
  const me = useMe();
  const canUpdate = useCan('admin:update') && me.tenantWide;
  const q = useQuery({ ...schedulesQuery, enabled: me.tenantWide });
  const invalidate = [['admin', 'schedule']];
  const pause = useActionMutation(pauseSchedule, { invalidate });
  const resume = useActionMutation(resumeSchedule, { invalidate });
  const run = useActionMutation(runSchedule, {
    invalidate: [...invalidate, ['admin', 'job']],
    success: t('schedules.started'),
    onSuccess: (r) => void navigate({ to: '/admin/jobs/$id', params: { id: r.workflowId } }),
  });
  if (!me.tenantWide) return <ErrorState title={t('schedules.title')} description={t('schedules.tenantWideOnly')} />;
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  return (
    <>
      <PageHeader eyebrow={t('nav.admin')} title={t('schedules.title')} description={t('schedules.description')} />
      {!q.data.items.length ? (
        <EmptyState title={t('schedules.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('schedules.name')}</Th>
              <Th>{t('schedules.cron')}</Th>
              <Th>{t('common.status')}</Th>
              <Th className="hidden md:table-cell">{t('schedules.lastRun')}</Th>
              <Th className="hidden md:table-cell">{t('schedules.nextRun')}</Th>
              {canUpdate && <Th className="text-right">{t('common.actions')}</Th>}
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((s) => (
              <Tr key={s.name}>
                <Td>
                  <Mono className="text-xs">{s.name}</Mono>
                </Td>
                <Td>
                  <Mono className="text-xs">{s.cron}</Mono>
                  <div className="text-[11px] text-fg-3">{s.timezone ?? 'UTC'}</div>
                </Td>
                <Td>{s.status === 'active' ? <Badge tone="success">{t('schedules.active')}</Badge> : <Badge tone="warning">{t('schedules.pausedState')}</Badge>}</Td>
                <Td className="hidden md:table-cell">
                  {s.lastRun ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      <Link to="/admin/jobs/$id" params={{ id: s.lastRun.workflowId }} className="tabular text-fg-2 hover:underline">
                        {formatDateTime(s.lastRun.createdAt)}
                      </Link>
                      <JobStatusBadge status={s.lastRun.status} />
                    </span>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="hidden md:table-cell tabular text-fg-2">{s.nextFireAt ? formatDateTime(s.nextFireAt) : '—'}</Td>
                {canUpdate && (
                  <Td className="text-right">
                    <div className="flex justify-end gap-2">
                      {s.status === 'active' ? (
                        <Button size="sm" variant="secondary" loading={pause.isPending && pause.variables?.input.name === s.name} onClick={() => pause.mutate({ input: { name: s.name } })}>
                          <Pause /> {t('schedules.pause')}
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary" loading={resume.isPending && resume.variables?.input.name === s.name} onClick={() => resume.mutate({ input: { name: s.name } })}>
                          <Play /> {t('schedules.resume')}
                        </Button>
                      )}
                      <Button size="sm" loading={run.isPending && run.variables?.input.name === s.name} onClick={() => run.mutate({ input: { name: s.name } })}>
                        <Zap /> {t('schedules.runNow')}
                      </Button>
                    </div>
                  </Td>
                )}
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </>
  );
}
